if (!customElements.get('pc-menu-bands-controller')) {
  class PCMenuBandsController extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cleanup = [];
      this.querySelectorAll('.pc-menu-bands__nav-link').forEach((link) => {
        const target = link.getAttribute('href');
        if (target?.startsWith('#') && !this.querySelector(target)) link.remove();
      });
      this.querySelectorAll('[data-pc-menu-band]').forEach((band) => this.setupBand(band));
      this.productIds = new Set(
        Array.from(this.querySelectorAll('[data-quick-add-holder]')).map((holder) => holder.dataset.quickAddHolder)
      );
      this.productData = new Map();

      this.handleMenuVariantChange = (event) => {
        if (!event.target.closest('variant-selects')) return;
        const modal = event.target.closest('[data-quick-add-modal].product-quick-add--pc-menu');
        if (!modal || !this.productIds.has(modal.dataset.productId)) return;
        this.updateMenuDrawerVariant(modal);
      };
      document.addEventListener('change', this.handleMenuVariantChange);
      this.cleanup.push(() => document.removeEventListener('change', this.handleMenuVariantChange));

      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
        const unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, () => {
          document
            .querySelectorAll('[data-quick-add-modal].product-quick-add--pc-menu[open]')
            .forEach((modal) => {
              if (!this.productIds.has(modal.dataset.productId)) return;
              this.scheduleMenuDrawerVariantUpdate(modal);
            });
        });
        this.cleanup.push(unsubscribe);
      }

      this.handleQuickAddOpen = () => {
        document
          .querySelectorAll('[data-quick-add-modal].product-quick-add--pc-menu[open]')
          .forEach((modal) => {
            if (!this.productIds.has(modal.dataset.productId)) return;
            this.prepareMenuDrawer(modal);
          });
      };
      document.addEventListener('theme:quick-add:open', this.handleQuickAddOpen);
      this.cleanup.push(() => document.removeEventListener('theme:quick-add:open', this.handleQuickAddOpen));

      this.handleProductSubmit = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const modal = form.closest('[data-quick-add-modal]');
        if (!modal || !this.productIds.has(modal.dataset.productId)) return;

        const preorderProperty = modal.querySelector(
          `[data-product-preorder][form="${CSS.escape(form.id)}"]`
        );
        if (!preorderProperty) return;

        const selectedValues = Array.from(form.elements)
          .filter((field) => {
            if (field instanceof HTMLInputElement && ['radio', 'checkbox'].includes(field.type)) {
              return field.checked;
            }
            return field instanceof HTMLSelectElement;
          })
          .map((field) => field.value.toLowerCase());

        preorderProperty.disabled = !selectedValues.some((value) => value.includes('pre-order'));
      };
      document.addEventListener('submit', this.handleProductSubmit, true);
      this.cleanup.push(() => document.removeEventListener('submit', this.handleProductSubmit, true));
    }

    disconnectedCallback() {
      this.restoreGloboVariantSelector();
      this.cleanup?.forEach((removeListener) => removeListener());
      this.cleanup = [];
      this.initialized = false;
    }

    setupBand(band) {
      const track = band.querySelector('[data-pc-menu-track]');
      const previous = band.querySelector('[data-pc-menu-prev]');
      const next = band.querySelector('[data-pc-menu-next]');

      if (!track || !previous || !next) return;

      const updateButtons = () => {
        const end = track.scrollWidth - track.clientWidth - 2;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= end;
      };

      const scroll = (direction) => {
        track.scrollBy({left: direction * track.clientWidth * 0.82, behavior: 'smooth'});
      };

      previous.addEventListener('click', () => scroll(-1));
      next.addEventListener('click', () => scroll(1));
      track.addEventListener('scroll', updateButtons, {passive: true});
      window.addEventListener('resize', updateButtons, {passive: true});
      this.cleanup.push(() => window.removeEventListener('resize', updateButtons));
      updateButtons();
    }

    getSelectedOptions(modal) {
      return Array.from(modal.querySelectorAll('variant-selects .selector-wrapper')).map((wrapper) => {
        const input = wrapper.querySelector('input[type="radio"]:checked, select, [data-popout-input]');
        return input?.value || '';
      });
    }

    scheduleMenuDrawerVariantUpdate(modal) {
      window.clearTimeout(modal.pcVariantUpdateTimer);
      modal.pcVariantUpdateTimer = window.setTimeout(() => {
        this.updateMenuDrawerVariant(modal);
        this.hidePurchaseContextSelector(modal);
      }, 0);
    }

    getMacroData(modal) {
      if (modal.pcMacroData) return modal.pcMacroData;
      const source = modal.querySelector('[data-pc-variant-macros]');
      try {
        modal.pcMacroData = new Map(
          JSON.parse(source?.textContent || '[]').map((entry) => [String(entry.id), entry])
        );
      } catch (error) {
        console.warn('Unable to read the menu nutrition data', error);
        modal.pcMacroData = new Map();
      }
      return modal.pcMacroData;
    }

    ensureMacroPanel(modal) {
      let panel = modal.querySelector('[data-pc-drawer-macros]');
      if (panel) return panel;

      panel = document.createElement('dl');
      panel.className = 'pc-menu-drawer__macros';
      panel.dataset.pcDrawerMacros = '';
      panel.setAttribute('aria-label', 'Nutrition per serving');
      panel.innerHTML = `
        <div><dt>Calories</dt><dd data-pc-macro="calories">—</dd></div>
        <div><dt>Protein</dt><dd data-pc-macro="protein">—</dd></div>
        <div><dt>Carbs</dt><dd data-pc-macro="carbohydrates">—</dd></div>
        <div><dt>Fat</dt><dd data-pc-macro="fat">—</dd></div>
      `;

      const priceBlock = modal.querySelector('.product__price__wrap')?.closest('.product__block');
      priceBlock?.insertAdjacentElement('afterend', panel);
      return panel;
    }

    updateDrawerMacros(modal, variantId) {
      const panel = this.ensureMacroPanel(modal);
      const macros = this.getMacroData(modal).get(String(variantId));
      const fields = ['calories', 'protein', 'carbohydrates', 'fat'];
      const hasMacros = Boolean(macros && fields.some((field) => macros[field] !== null));

      panel.hidden = !hasMacros;
      fields.forEach((field) => {
        const value = macros?.[field];
        const element = panel.querySelector(`[data-pc-macro="${field}"]`);
        if (!element) return;
        element.textContent = value === null || value === undefined ? '—' : `${value}${field === 'calories' ? '' : 'g'}`;
      });
    }

    hidePurchaseContextSelector(modal) {
      if (!['in_store', 'pre_order'].includes(modal.dataset.pcPurchaseContext)) return;

      modal.querySelectorAll('variant-selects .selector-wrapper').forEach((wrapper) => {
        const optionName = wrapper.querySelector('.radio__legend__label')?.textContent?.toLowerCase() || '';
        const optionValues = Array.from(wrapper.querySelectorAll('input, [data-popout-input]'))
          .map((input) => input.value?.toLowerCase() || '')
          .join(' ');
        const isContext =
          optionName.includes('pre-order') ||
          optionName.includes('pick up now') ||
          optionValues.includes('pre-order') && (optionValues.includes('in-store') || optionValues.includes('pickup'));

        if (isContext) {
          wrapper.hidden = true;
          wrapper.dataset.pcContextSelector = '';
        }
      });
    }

    selectInitialVariant(modal, product, variant) {
      if (!variant || modal.dataset.pcInitialVariantApplied === 'true') return;
      const wrappers = Array.from(modal.querySelectorAll('variant-selects .selector-wrapper'));
      let changeTarget = null;

      variant.options.forEach((option, index) => {
        const wrapper = wrappers.find((candidate) => Number(candidate.dataset.optionPosition) === index + 1);
        if (!wrapper) return;

        const radio = Array.from(wrapper.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === option
        );
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            changeTarget = radio;
          }
          return;
        }

        const select = wrapper.querySelector('select');
        if (select && select.value !== option) {
          select.value = option;
          changeTarget = select;
          return;
        }

        const popoutInput = wrapper.querySelector('[data-popout-input]');
        if (popoutInput && popoutInput.value !== option) {
          const optionLink = Array.from(wrapper.querySelectorAll('[data-popout-option]')).find(
            (link) => link.dataset.value === option
          );
          popoutInput.value = option;
          if (optionLink?.dataset.optionValueId) {
            popoutInput.dataset.optionValueId = optionLink.dataset.optionValueId;
          }
          changeTarget = popoutInput;
        }
      });

      modal.dataset.pcInitialVariantApplied = 'true';
      if (changeTarget) changeTarget.dispatchEvent(new Event('change', {bubbles: true}));
    }

    async prepareMenuDrawer(modal) {
      if (modal.dataset.pcPreparing === 'true') return;
      modal.dataset.pcPreparing = 'true';
      const productInfo = modal.querySelector('product-info');
      const handle = productInfo?.dataset.productHandle;
      if (!handle) {
        delete modal.dataset.pcPreparing;
        return;
      }

      try {
        if (modal.dataset.pcVariantListenerAttached !== 'true') {
          modal.dataset.pcVariantListenerAttached = 'true';
          modal.addEventListener('change', (event) => {
            if (!event.target.closest('variant-selects')) return;
            this.scheduleMenuDrawerVariantUpdate(modal);
            [120, 500, 1000].forEach((delay) => {
              window.setTimeout(() => this.scheduleMenuDrawerVariantUpdate(modal), delay);
            });
          });
        }

        const product = await this.getProductData(handle);
        const initialVariant = product.variants.find(
          (variant) => String(variant.id) === String(modal.dataset.pcInitialVariantId)
        );
        this.selectInitialVariant(modal, product, initialVariant);
        this.hidePurchaseContextSelector(modal);
        await this.updateMenuDrawerVariant(modal);
        await this.ensureGloboQuickView(modal, handle);

        [100, 350].forEach((delay) => {
          window.setTimeout(() => this.hidePurchaseContextSelector(modal), delay);
        });
      } catch (error) {
        console.warn('Unable to prepare the menu drawer', error);
      } finally {
        delete modal.dataset.pcPreparing;
      }
    }

    async getProductData(handle) {
      if (!this.productData.has(handle)) {
        const request = fetch(`${window.theme.routes.root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Unable to load ${handle}`);
            return response.json();
          })
          .catch((error) => {
            this.productData.delete(handle);
            throw error;
          });
        this.productData.set(handle, request);
      }
      return this.productData.get(handle);
    }

    async ensureGloboQuickView(modal, handle) {
      if (modal.dataset.pcPurchaseContext !== 'pre_order') return;
      const form = Array.from(modal.querySelectorAll('form[action*="/cart/add"]')).find(
        (candidate) =>
          !candidate.matches('.installment, [data-product-form-installment]') &&
          candidate.querySelector('[data-add-to-cart], button[name="add"][type="submit"]')
      );
      if (!form) return;

      this.deactivateOtherGloboDrawers(modal);

      const formKey = `pc-gpo-${modal.dataset.productId || Date.now()}`;
      form.dataset.pcGpoQuickviewForm = formKey;
      const selector = `[data-pc-gpo-quickview-form="${formKey}"]`;

      let globoVariantScoped = this.scopeGloboVariantSelector(modal, form, formKey);

      let existingWidget = modal.querySelector('.gpo-app');
      const widgetAlreadyUsesProductForm = Boolean(form.querySelector('.gpo-hidden-inputs'));
      if (existingWidget?.querySelector('.gpo-element') && widgetAlreadyUsesProductForm) {
        existingWidget.classList.add('pc-menu-globo-options');
        this.normalizeGloboOptionLabels(existingWidget);
        this.attachGloboPriceSync(modal, existingWidget);
        this.updateMenuDrawerGloboPrice(modal);
        return;
      }

      // Globo can auto-initialize against Shopify's installment form before Fast Add is ready.
      // Discard that invalid instance so the official Quick View initializer can bind to the
      // real product form (the one that owns the Add to Cart button).
      if (existingWidget) {
        existingWidget.remove();
        existingWidget = null;
        modal.querySelectorAll('.gpo-hidden-inputs').forEach((node) => node.remove());
        modal.querySelectorAll('[gpo-quickview-loaded]').forEach((node) => {
          node.removeAttribute('gpo-quickview-loaded');
        });
      }

      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (window.GPOConfigs?.handleQuickViewActivatorClick) break;
        if (window.GPOConfigs?.loadQuickViewScripts && attempt === 0) {
          try {
            await window.GPOConfigs.loadQuickViewScripts();
          } catch (error) {
            console.warn('Unable to load Globo Quick View support', error);
          }
        }
        await new Promise((resolve) => window.setTimeout(resolve, 150));
      }

      if (!window.GPOConfigs?.handleQuickViewActivatorClick) {
        console.warn('Globo Quick View support is unavailable in Fast Add');
        return;
      }

      if (!globoVariantScoped) {
        globoVariantScoped = this.scopeGloboVariantSelector(modal, form, formKey);
      }
      if (!globoVariantScoped) {
        console.warn(`Unable to scope Globo's variant selector for ${handle}`);
        return;
      }

      await window.GPOConfigs.handleQuickViewActivatorClick(handle, {
        quickViewProductForm: selector
      });

      for (let attempt = 0; attempt < 24; attempt += 1) {
        const widget = modal.querySelector('.gpo-app');
        if (widget?.querySelector('.gpo-element')) {
          widget.classList.add('pc-menu-globo-options');
          this.normalizeGloboOptionLabels(widget);
          this.attachGloboPriceSync(modal, widget);
          this.updateMenuDrawerGloboPrice(modal);
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 150));
      }

      console.warn(`Globo did not render options for ${handle} inside Fast Add`);
    }

    deactivateOtherGloboDrawers(activeModal) {
      document
        .querySelectorAll('[data-quick-add-modal].product-quick-add--pc-menu')
        .forEach((modal) => {
          if (modal === activeModal) return;

          modal.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
            form.querySelectorAll('.gpo-hidden-inputs').forEach((node) => node.remove());
            form.removeAttribute('gpo-quickview-loaded');
            delete form.dataset.gpoQuickviewLoaded;
          });
          modal.querySelectorAll('.gpo-app').forEach((node) => node.remove());
        });
    }

    scopeGloboVariantSelector(modal, form, formKey) {
      const configs = window.GPOConfigs;
      const variantInput = form.querySelector('input[name="id"]');
      if (!configs?.theme?.product || !variantInput) return false;

      if (!this.pcOriginalGloboVariantSelector) {
        this.pcOriginalGloboVariantSelector = configs.theme.product.variantSelector;
      }

      if (this.pcGloboVariantObserver) {
        this.pcGloboVariantObserver.disconnect();
        this.pcGloboVariantObserver = null;
      }

      variantInput.dataset.pcGpoVariantSelector = formKey;
      const scopedSelector = `[data-pc-gpo-variant-selector="${formKey}"]`;
      configs.theme.product.variantSelector = scopedSelector;
      this.pcActiveGloboModal = modal;
      this.pcActiveGloboVariantSelector = scopedSelector;

      this.pcGloboVariantObserver = new MutationObserver(() => {
        if (modal.isConnected) return;
        this.restoreGloboVariantSelector(modal);
      });
      this.pcGloboVariantObserver.observe(document.body, {childList: true, subtree: true});
      return true;
    }

    restoreGloboVariantSelector(modal = null) {
      if (modal && this.pcActiveGloboModal !== modal) return;

      const configs = window.GPOConfigs;
      if (
        configs?.theme?.product &&
        this.pcOriginalGloboVariantSelector &&
        configs.theme.product.variantSelector === this.pcActiveGloboVariantSelector
      ) {
        configs.theme.product.variantSelector = this.pcOriginalGloboVariantSelector;
      }

      this.pcGloboVariantObserver?.disconnect();
      this.pcGloboVariantObserver = null;
      this.pcActiveGloboModal = null;
      this.pcActiveGloboVariantSelector = null;
    }

    attachGloboPriceSync(modal, widget) {
      if (widget.dataset.pcPriceSyncAttached === 'true') return;
      widget.dataset.pcPriceSyncAttached = 'true';

      const scheduleUpdate = () => {
        [0, 100, 350].forEach((delay) => {
          window.setTimeout(() => this.updateMenuDrawerGloboPrice(modal), delay);
        });
      };

      widget.addEventListener('change', scheduleUpdate);
      widget.addEventListener('input', scheduleUpdate);

      const total = widget.querySelector('.gpo-total-text');
      if (total) {
        const observer = new MutationObserver(scheduleUpdate);
        observer.observe(total, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      const optionObserver = new MutationObserver(() => {
        this.normalizeGloboOptionLabels(widget);
      });
      optionObserver.observe(widget, {childList: true, subtree: true});
    }

    normalizeGloboOptionLabels(widget) {
      const dietaryInput = widget.querySelector('input[data-field-name="dietary-preferences"]');
      const dietaryGroup = dietaryInput?.closest('.gpo-element');
      const heading = dietaryGroup?.querySelector('.gpo-label .label-content');

      if (heading && heading.textContent.trim() !== 'Dietary Preferences') {
        heading.textContent = 'Dietary Preferences';
      }
    }

    parseMoneyToCents(value) {
      const raw = String(value || '').replace(/[^0-9,.-]/g, '');
      if (!raw) return 0;

      const lastDot = raw.lastIndexOf('.');
      const lastComma = raw.lastIndexOf(',');
      const decimalMark = lastDot > lastComma ? '.' : ',';
      const decimalIndex = raw.lastIndexOf(decimalMark);
      const hasDecimal = decimalIndex >= 0 && raw.length - decimalIndex - 1 === 2;
      const normalized = hasDecimal
        ? `${raw.slice(0, decimalIndex).replace(/[.,]/g, '')}.${raw.slice(decimalIndex + 1)}`
        : raw.replace(/[.,]/g, '');
      const amount = Number(normalized);
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }

    updateMenuDrawerGloboPrice(modal) {
      const basePrice = Number(modal.dataset.pcBaseVariantPrice);
      if (!Number.isFinite(basePrice)) return;

      const addOnMoney = modal.querySelector('.gpo-app .gpo-total-text .money');
      const addOnPrice = this.parseMoneyToCents(addOnMoney?.textContent);
      const totalPrice = window.theme.formatMoney(basePrice + addOnPrice, window.theme.moneyFormat);

      modal.querySelectorAll('[data-product-price]').forEach((element) => {
        element.textContent = totalPrice;
      });
    }

    async updateMenuDrawerVariant(modal) {
      const productInfo = modal.querySelector('product-info');
      const handle = productInfo?.dataset.productHandle;
      const selectedOptions = this.getSelectedOptions(modal);
      if (!handle || selectedOptions.some((value) => !value)) return;

      const selectionKey = selectedOptions.join('\u001f');

      try {
        const product = await this.getProductData(handle);
        if (this.getSelectedOptions(modal).join('\u001f') !== selectionKey) return;

        const variant = product.variants.find((candidate) =>
          candidate.options.every((option, index) => option === selectedOptions[index])
        );
        if (!variant) return;

        const price = window.theme.formatMoney(variant.price, window.theme.moneyFormat);
        const compareAtPrice = variant.compare_at_price && variant.compare_at_price > variant.price
          ? window.theme.formatMoney(variant.compare_at_price, window.theme.moneyFormat)
          : '';

        modal.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', {bubbles: true}));
        });

        const isPreorderVariant = variant.options.some((option) => option.toLowerCase().includes('pre-order'));
        modal.querySelectorAll('[data-product-preorder]').forEach((input) => {
          input.disabled = !isPreorderVariant;
        });

        modal.querySelectorAll('[data-selected-variant]').forEach((script) => {
          script.textContent = JSON.stringify(variant);
        });

        this.updateDrawerMacros(modal, variant.id);
        this.hidePurchaseContextSelector(modal);
        modal.dataset.pcBaseVariantPrice = String(variant.price);
        modal.querySelectorAll('[data-product-price]').forEach((element) => {
          element.textContent = price;
        });

        [0, 150, 450].forEach((delay) => {
          window.setTimeout(() => this.updateMenuDrawerGloboPrice(modal), delay);
        });

        const priceRoot = modal.querySelector('.product__price');
        const regularPrice = priceRoot?.querySelector('.price-item--regular');
        const salePrice = priceRoot?.querySelector('.price-item--sale');
        if (regularPrice) regularPrice.textContent = compareAtPrice || price;
        if (salePrice) salePrice.textContent = price;
        priceRoot?.classList.toggle('price--on-sale', Boolean(compareAtPrice));

        const submitButton = modal.querySelector('[data-add-to-cart]');
        if (submitButton) {
          submitButton.disabled = !variant.available;
          submitButton.setAttribute('aria-disabled', String(!variant.available));
          const buttonText = submitButton.querySelector('[data-add-to-cart-text]');
          const labelNode = buttonText
            ? Array.from(buttonText.childNodes).find((node) => node.nodeType === Node.TEXT_NODE)
            : null;
          if (labelNode) labelNode.textContent = variant.available ? 'Add to cart ' : 'Sold out ';
        }
      } catch (error) {
        console.warn('Unable to update the menu drawer variant', error);
      }
    }
  }

  customElements.define('pc-menu-bands-controller', PCMenuBandsController);
}
